import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiDateFieldFormController,
  MuiGrid,
  MuiTextField,
} from "../../../../../components";
import { ClientAutoComplete } from "../../../../../components/clientAutoComplete/ClientAutoComplete";
import {
  CLIENT_TYPE_SITE_CORE_VALUES,
  gridSize,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { NCBPReportFormKeys } from "../../../modelsAndSchemas/models";
import { PendingBillingSearchFormWrapper } from "../../../ReportSearchStyle";
import { reportsSearchReportsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

const pageId = "ncbpReport";
export interface NCBPReportFormRef {
  reset: () => void;
}

export interface NCBPReportFormProps {
  onResetSearch: () => void;
  onSubmit: () => void;
}

export const NCBPReportForm = forwardRef<
  NCBPReportFormRef,
  NCBPReportFormProps
>(({ onResetSearch, onSubmit }, ref) => {
  const { fetchComponentTitle, commonLabels, useFetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { isMobile } = useMediaQueries();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const actualClientRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const nonClientBillingPartyRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const {
    reset,
    control,
    clearErrors,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  useImperativeHandle(ref, () => ({
    reset: handleClearFilter,
  }));

  const dossierNumberWatch = useWatch({
    control,
    name: NCBPReportFormKeys.dossierNumber,
  });

  const invoiceStartDateWatch = useWatch({
    control,
    name: NCBPReportFormKeys.invoiceStartDate,
  });

  const invoiceEndDateWatch = useWatch({
    control,
    name: NCBPReportFormKeys.invoiceEndDate,
  });

  const isDossierNumberAvailable = useMemo(
    () => isNotEmpty(dossierNumberWatch),
    [dossierNumberWatch],
  );

  useEffect(() => {
    if (isNotEmpty(dossierNumberWatch) && isNotEmpty(errors)) {
      clearErrors([
        NCBPReportFormKeys.invoiceStartDate,
        NCBPReportFormKeys.invoiceEndDate,
      ]);
    }
  }, [dossierNumberWatch, errors]);

  useEffect(() => {
    if (isEmpty(invoiceStartDateWatch) && isNotEmpty(invoiceEndDateWatch)) {
      setValue(NCBPReportFormKeys.invoiceEndDate, null);
    }
  }, [invoiceStartDateWatch, invoiceEndDateWatch]);

  const minInvoiceEndDate = useMemo(() => {
    if (isEmpty(invoiceStartDateWatch)) {
      return undefined;
    }
    const startDate = dayjs(
      invoiceStartDateWatch as unknown as string,
      userCountryDateFormat,
    );
    return startDate;
  }, [invoiceStartDateWatch, userCountryDateFormat]);

  const maxInvoiceEndDate = useMemo(() => {
    if (isEmpty(invoiceStartDateWatch)) {
      return undefined;
    }
    const startDate = dayjs(
      invoiceStartDateWatch as unknown as string,
      userCountryDateFormat,
    );
    return startDate.add(365, "day");
  }, [invoiceStartDateWatch, userCountryDateFormat]);

  const handleClearFilter = () => {
    clearErrors();
    reset();
    actualClientRef.current?.resetValues();
    nonClientBillingPartyRef.current?.resetValues();
    onResetSearch();
  };

  const getLabel = useFetchLabel(reportsSearchReportsSiteCoreKeys.header);

  const getDateValue = (key: keyof typeof NCBPReportFormKeys) => {
    if (isEmpty(getValues(key))) {
      return null;
    }
    const value = getValues(key) as unknown as string;
    return dayjs(value, userCountryDateFormat);
  };

  const renderNCBPReportHeader = () => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid justifySelf="end" className="form-header-right">
          <MuiButtonTertiary onClick={handleClearFilter}>
            <RefreshIcon /> {commonLabels.resetAllSearch}
          </MuiButtonTertiary>
        </MuiGrid>
      </MuiGrid>
    );
  };

  return (
    <PendingBillingSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={true}
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(reportsSearchReportsSiteCoreKeys.header)}
          />
        }
      >
        {renderNCBPReportHeader()}
        <MuiBox className="selectReportFieldContainer">
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 4}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={NCBPReportFormKeys.dossierNumber}
              >
                <MuiTextField
                  id={NCBPReportFormKeys.dossierNumber}
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.dossierNumber,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  showCloseIcon
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={NCBPReportFormKeys.actualClientId}
                ref={actualClientRef}
              >
                <ClientAutoComplete
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.actualClient,
                  )}
                  formId={NCBPReportFormKeys.actualClientId}
                  control={control}
                  infoMessage={getLabel(
                    reportsSearchReportsSiteCoreKeys.actualClientNotificationIconMessage,
                  )}
                  isActiveClientOnly
                  excludeNCBPClient
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={NCBPReportFormKeys.nonClientBillingPartyId}
                ref={nonClientBillingPartyRef}
              >
                <ClientAutoComplete
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.nonClientBillingParty,
                  )}
                  formId={NCBPReportFormKeys.nonClientBillingPartyId}
                  control={control}
                  isActiveClientOnly
                  excludeNCBPClient={false}
                  clientType={
                    CLIENT_TYPE_SITE_CORE_VALUES.NON_CLIENT_BILLING_PARTY
                  }
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={NCBPReportFormKeys.invoiceStartDate}
                id={`${pageId}-${NCBPReportFormKeys.invoiceStartDate}`}
                value={getDateValue(NCBPReportFormKeys.invoiceStartDate)}
                label={getLabel(
                  reportsSearchReportsSiteCoreKeys.invoiceStartDate,
                )}
                valueFormat={userCountryDateFormat}
                clearable
                className="date-picker"
                disabled={isDossierNumberAvailable}
                isMandatory={!isDossierNumberAvailable}
                format={userCountryDateFormat}
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={NCBPReportFormKeys.invoiceEndDate}
                id={`${pageId}-${NCBPReportFormKeys.invoiceEndDate}`}
                value={getDateValue(NCBPReportFormKeys.invoiceEndDate)}
                label={getLabel(
                  reportsSearchReportsSiteCoreKeys.invoiceEndDate,
                )}
                valueFormat={userCountryDateFormat}
                clearable
                className="date-picker"
                disabled={
                  isDossierNumberAvailable || isEmpty(invoiceStartDateWatch)
                }
                format={userCountryDateFormat}
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
                isMandatory={!isDossierNumberAvailable}
                infoMessage={getLabel(
                  reportsSearchReportsSiteCoreKeys.invoiceEndDateNotificationIconMessage,
                )}
                minDate={minInvoiceEndDate}
                maxDate={maxInvoiceEndDate}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="selectReportButtonContainer" textAlign="right">
          <MuiButtonPrimary onClick={onSubmit}>
            {commonLabels.search}
          </MuiButtonPrimary>
        </MuiBox>
      </AccordionHeader>
    </PendingBillingSearchFormWrapper>
  );
});
